import {
  Typography,
  TypographySize,
  TypographyWeight,
} from "@beydesign/storybook"
import { Player } from "@lottiefiles/react-lottie-player"
import { Box, Stack, useMediaQuery } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import React, { useEffect, useState } from "react"
import CallLoadingAnimation from "../../assets/call-loading.json"
import { Colors, useCustomBranding } from "../../constants/colors"
import { getViewWidth } from "../../constants/styles"
import { Agent } from "../../types/Agent"
import { isInIframe } from "../../utils/func"

interface CallLoadingProps {
  agent?: Agent
}

const AnimatedText = ({
  showAgent,
  agentName,
}: {
  showAgent: boolean
  agentName?: string
}) => (
  <Typography
    color={Colors.Purple}
    size={TypographySize.HeadingL}
    weight={TypographyWeight.Medium}
    textStyle={{
      marginTop: "2px",
      textAlign: "center",
      position: "relative",
      width: "100%",
    }}
  >
    <span
      style={{
        transition: "opacity 0.6s ease-in-out",
        opacity: showAgent && agentName ? 1 : 0,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
      }}
    >
      {agentName} is joining now!
    </span>
    <span
      style={{
        transition: "opacity 0.3s ease-in-out",
        opacity: showAgent && agentName ? 0 : 1,
      }}
    >
      Getting ready for your call!
    </span>
    <br /> Stay tuned.
  </Typography>
)

export default function CallLoading({ agent }: CallLoadingProps) {
  const theme = useTheme()
  const isEmbed = isInIframe()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const { isEnabled } = useCustomBranding()
  const [showAgentName, setShowAgentName] = useState(false)

  useEffect(() => {
    if (agent?.name) {
      const timer = setTimeout(() => {
        setShowAgentName(true)
      }, 1200)
      return () => clearTimeout(timer)
    }
  }, [agent?.name])

  const topOffset = isEmbed ? 0 : isMobile ? "5vh" : "10vh"
  const sideOffset = isEmbed ? 0 : isMobile ? "10px" : "30px"

  return (
    <Stack
      direction={"column"}
      position={"fixed"}
      top={topOffset}
      left={sideOffset}
      right={sideOffset}
      height={isEmbed ? "100%" : `calc(100% - ${topOffset})`}
      overflow={"hidden"}
      zIndex={1000}
    >
      <Box
        height={"100%"}
        minHeight={"500px"}
        width={"100%"}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        justifyContent={"flex-start"}
        position={"relative"}
        sx={{
          padding: isEmbed ? 0 : { xs: "5px", sm: "5px 10px 10px 10px" },
        }}
      >
        {isMobile ? (
          // Mobile Layout
          <Box
            display={"flex"}
            flexDirection={"column"}
            height={"100%"}
            width={"100%"}
          >
            <Box flex={1} position={"relative"}>
              <div
                className="lk-video-conference-inner"
                style={{
                  height: "100%",
                  width: "100%",
                  position: "relative",
                  minHeight: "400px",
                }}
              >
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  position={"relative"}
                  borderRadius={isEmbed ? 0 : "1rem"}
                  overflow={"hidden"}
                  width={"100%"}
                  height={"100%"}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: isEmbed ? 0 : "12px",
                      background: isEnabled
                        ? "white"
                        : "var(--primitives-desktop-gradient-soft-ambient-light)",
                      overflow: "hidden",
                    }}
                  >
                    <Player
                      autoplay
                      loop
                      src={CallLoadingAnimation}
                      style={{ width: "100%" }}
                    />
                    <AnimatedText
                      showAgent={showAgentName}
                      agentName={agent?.name}
                    />
                  </Box>
                </Box>
              </div>
            </Box>
          </Box>
        ) : (
          // Desktop Layout
          <div
            className="lk-video-conference-inner"
            style={{
              width: getViewWidth(isEmbed),
              marginRight: "unset",
              margin: "0px auto",
              height: "100%",
              minHeight: "500px",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              position={"relative"}
              borderRadius={isEmbed ? 0 : "1rem"}
              overflow={"hidden"}
              width={"100%"}
              height={"100%"}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: isEmbed ? 0 : "12px",
                  background: isEnabled
                    ? "white"
                    : "var(--primitives-desktop-gradient-soft-ambient-light)",
                  overflow: "hidden",
                }}
              >
                <Player
                  autoplay
                  loop
                  src={CallLoadingAnimation}
                  style={{ width: "100%", maxWidth: "400px" }}
                />
                <AnimatedText
                  showAgent={showAgentName}
                  agentName={agent?.name}
                />
              </Box>
            </Box>
          </div>
        )}
      </Box>
    </Stack>
  )
}
